import styled from 'styled-components'
import { motion } from 'framer-motion'
import { colors, getFontStyle } from '@diversioteam/diversio-ds'

import type { DropdownContainerProps, NotificationListProps, EmptyStateProps } from './NotificationDropdown.types'

export const DropdownContainer = styled(motion.div)<DropdownContainerProps>`
    position: absolute;
    top: calc(100% + 1.75rem);
    right: 0;
    width: 512px;
    max-width: calc(100vw - 2rem);
    max-height: 640px;
    background: ${colors.primaryWhite};
    border: 1px solid ${colors.primaryGray2};
    border-radius: 0.75rem;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
    z-index: 50;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    /* a11y: identify as dialog container visually */

    ${(props) =>
        !props.$isOpen &&
        `
    pointer-events: none;
  `}
`

export const DropdownHeader = styled.div`
    padding: 4px 20px;
    border-bottom: 1px solid ${colors.primaryGray1};
`

export const HeaderTitle = styled.h3`
    ${getFontStyle('p03Medium')}
    color: ${colors.primary60};
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
`

export const HeaderActions = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`

export const HeaderButton = styled.button`
    ${getFontStyle('p03Regular')}
    padding: 0.25rem 0.5rem;
    border: none;
    background: transparent;
    color: ${colors.primary70};
    cursor: pointer;
    border-radius: 0.25rem;
    transition: all 0.2s ease;

    &:hover {
        background: ${colors.primaryGray1};
        color: ${colors.primary80};
    }

    &:active {
        background: ${colors.primaryGray2};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

export const NotificationList = styled(motion.div)<NotificationListProps>`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden; /* Explicitly prevent horizontal scroll */
    position: relative;
    background-color: ${colors.primaryGray1};

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: ${colors.primaryGray1};
    }

    &::-webkit-scrollbar-thumb {
        background: ${colors.primary50};
        border-radius: 3px;

        &:hover {
            background: ${colors.primary60};
        }
    }

    ${(props) =>
        props.$isEmpty &&
        `
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 8rem;
  `}

    /* Subtle bottom fade to hint scroll */
    &::after {
        content: '';
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        height: 20px;
        pointer-events: none;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${colors.primaryWhite} 100%);
    }
`

export const StickyNewPill = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    padding: 0.25rem 0.75rem;
    background: linear-gradient(180deg, ${colors.primaryWhite} 60%, rgba(255, 255, 255, 0));
`

export const EmptyState = styled.div<EmptyStateProps>`
    text-align: center;
    padding: 2rem 1.5rem;
    color: ${colors.primary60};
`

export const EmptyStateIcon = styled.div`
    width: 48px;
    height: 48px;
    margin: 0 auto 1rem;
    color: ${colors.primaryGray2};
    display: flex;
    align-items: center;
    justify-content: center;
`

export const EmptyStateTitle = styled.h4`
    ${getFontStyle('p01Semibold')}
    color: ${colors.primary80};
    margin: 0 0 0.5rem 0;
`

export const EmptyStateDescription = styled.p`
    ${getFontStyle('p02Regular')}
    color: ${colors.primary70};
    margin: 0;
    line-height: 1.4;
`

export const LoadingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: ${colors.primary60};
    gap: 0.75rem;
`

export const LoadingSpinner = styled(motion.div)`
    width: 20px;
    height: 20px;
    border: 2px solid ${colors.primaryGray2};
    border-top-color: ${colors.primary60};
    border-radius: 50%;
`

// Animation variants for Framer Motion
/* Removed unused framer variants to keep file lean */
